import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, AlertCircle, Loader2, Save, Plus, Trash2, Building2, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { TitularPicker } from "@/components/TitularPicker";
import { ParteBloqueada, TramitadorAutorizadoField } from "@/components/TramitadorAutorizadoField";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import { PaisesPicker } from "@/components/PaisesPicker";
import { ResponsablePicker } from "@/components/ResponsablePicker";
import { useSeguridadAccess } from "@/hooks/useSeguridadAccess";

// ============================================================================
// Tipos
// ============================================================================
interface RegistroResumen { id: string; razonSocial: string; rfc: string; estatus: string; }
interface Servicio { codigo: string; nombre: string; descripcion: string; }
interface Pais { id: string; codigo: string; nombre: string; }
interface ContextoMio {
  titularId: string;
  titularRazonSocial: string;
  tramitadorId: string | null;
  tramitadorRazonSocial: string | null;
  titularPuedeActuarComoTitular: boolean;
  titularPuedeActuarComoTramitador: boolean;
}

interface Parametros {
  voltajeEntrada: string; voltajeSalida: string;
  corrienteEntrada: string; corrienteSalida: string;
  frecuenciaEntrada: string; frecuenciaSalida: string;
  potenciaEntrada: string; potenciaSalida: string;
}
const PARAMS_VACIOS: Parametros = {
  voltajeEntrada: "", voltajeSalida: "", corrienteEntrada: "", corrienteSalida: "",
  frecuenciaEntrada: "", frecuenciaSalida: "", potenciaEntrada: "", potenciaSalida: "",
};

interface AccesorioForm extends Parametros { uiId: string; nombre: string; }
interface FraccionForm { uiId: string; numero: string; nico: string; }
interface NormaForm { uiId: string; codigo: string; esquema: string; nmx: string[]; }
/** Entrada del catálogo de normas (con esquemas y NMX relacionadas). */
interface NormaCat { id: string; codigo: string; nombre: string; esquemas: string[]; nmx: string[]; }

const OTRO = "OTRO";
// Tipos que muestran datos de laboratorio (réplica de renderAllLaboratorio +
// renderDatosLaboratorio del portlet ServiciosSeguridadLG).
const SERVICIOS_CON_LAB = new Set(["CERTIFICACION_NUEVA", "RENOVACION", "AMPLIACION"]);
// Tipos que NO piden certificado anterior (renderCertificaVigente).
const SERVICIOS_SIN_CERT_ANTERIOR = new Set(["CERTIFICACION_NUEVA", "DICTAMEN_ESPECIAL", "DICTAMEN_TECNICO"]);

function uid(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ============================================================================
// Página principal
// ============================================================================
export function SolicitudSeguridadAltaPage() {
  const access = useSeguridadAccess();
  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Seguridad"
        sectorCodigo="SEGURIDAD"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }
  return <SolicitudSeguridadAltaForm />;
}

function SolicitudSeguridadAltaForm() {
  const navigate = useNavigate();
  const access = useSeguridadAccess();
  const [params] = useSearchParams();
  const titularIdInicial =
    params.get("titularId") ?? window.sessionStorage.getItem("sicen.contexto.titularId");

  // -- Estado --
  const [titular, setTitular] = useState<RegistroResumen | null>(null);
  const [tramitador, setTramitador] = useState<RegistroResumen | null>(null);
  // Cuántos tramitadores autorizados (carta poder vigente) tiene el titular
  // seleccionado; con >1 la selección restringida se vuelve obligatoria.
  const [numTramitadoresAut, setNumTramitadoresAut] = useState(0);
  const [servicio, setServicio] = useState("");
  const [otroServicio, setOtroServicio] = useState("");

  const [productoNombre, setProductoNombre] = useState("");
  const [marca, setMarca] = useState("");
  const [modelos, setModelos] = useState("");
  const [accesoriosTexto, setAccesoriosTexto] = useState("");
  const [producto, setProducto] = useState<Parametros>({ ...PARAMS_VACIOS });
  const [accesorios, setAccesorios] = useState<AccesorioForm[]>([]);

  const [paisesOrigen, setPaisesOrigen] = useState<string[]>([]);
  const [paisesProcedencia, setPaisesProcedencia] = useState<string[]>([]);
  const [fracciones, setFracciones] = useState<FraccionForm[]>([{ uiId: uid(), numero: "", nico: "" }]);

  const [labNombre, setLabNombre] = useState("");
  const [labNumInforme, setLabNumInforme] = useState("");
  const [labModelosEvaluados, setLabModelosEvaluados] = useState("");
  const [labFechaFinPruebas, setLabFechaFinPruebas] = useState("");
  const [labFechaEmision, setLabFechaEmision] = useState("");

  const [modelosAgrupar, setModelosAgrupar] = useState("");
  const [modeloRepresentativo, setModeloRepresentativo] = useState("");
  const [certAntNumDictamen, setCertAntNumDictamen] = useState("");
  const [certAntModsAmpliar, setCertAntModsAmpliar] = useState("");
  const [certAntCertCumpl, setCertAntCertCumpl] = useState("");

  const [normas, setNormas] = useState<NormaForm[]>([{ uiId: uid(), codigo: "", esquema: "", nmx: [] }]);
  const [observaciones, setObservaciones] = useState("");
  const [nombreRepresentante, setNombreRepresentante] = useState("");
  const [condicionesUso, setCondicionesUso] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // -- Catálogos --
  const qServicios = useQuery({
    queryKey: ["seguridad-servicios"],
    queryFn: async () => (await api.get<Servicio[]>("/api/sector/seguridad/catalogos/servicios")).data,
  });
  const qPaises = useQuery({
    queryKey: ["seguridad-paises"],
    queryFn: async () => (await api.get<Pais[]>("/api/sector/seguridad/catalogos/paises")).data,
  });
  const qNormasCat = useQuery({
    queryKey: ["seguridad-normas-catalogo"],
    queryFn: async () => (await api.get<NormaCat[]>("/api/sector/seguridad/catalogos/normas")).data,
  });
  const normaCatPorCodigo = (codigo: string): NormaCat | undefined =>
    qNormasCat.data?.find((x) => x.codigo === codigo);
  const qContextos = useQuery<ContextoMio[]>({
    queryKey: ["contextos-mios"],
    queryFn: async () => (await api.get<ContextoMio[]>("/api/contextos/mios")).data,
  });
  const ctxActivo = qContextos.data?.find((c) => c.titularId === titularIdInicial) ?? null;
  const ctxEsTramitadorPuro =
    !!ctxActivo && !ctxActivo.titularPuedeActuarComoTitular && ctxActivo.titularPuedeActuarComoTramitador;

  // Bloqueo de partes para usuarios externos (mismo criterio que Telecom): el
  // contexto (RL) fija titular y/o tramitador y no deben poder modificarse.
  const esExterno = !access.esInterno;
  const titularBloqueado = esExterno && !!ctxActivo?.titularPuedeActuarComoTitular;
  const tramitadorBloqueado = esExterno && (!!ctxActivo?.tramitadorId || ctxEsTramitadorPuro);

  useEffect(() => {
    if (!ctxActivo) return;
    if (!titular && ctxActivo.titularPuedeActuarComoTitular) {
      setTitular({ id: ctxActivo.titularId, razonSocial: ctxActivo.titularRazonSocial, rfc: "", estatus: "" });
    }
    if (!tramitador) {
      if (ctxActivo.tramitadorId && ctxActivo.tramitadorRazonSocial) {
        setTramitador({ id: ctxActivo.tramitadorId, razonSocial: ctxActivo.tramitadorRazonSocial, rfc: "", estatus: "" });
      } else if (ctxEsTramitadorPuro) {
        setTramitador({ id: ctxActivo.titularId, razonSocial: ctxActivo.titularRazonSocial, rfc: "", estatus: "" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxActivo]);

  const showLab = SERVICIOS_CON_LAB.has(servicio);
  const showCertAnterior = servicio !== "" && !SERVICIOS_SIN_CERT_ANTERIOR.has(servicio);

  // -- Submit --
  const crear = useMutation({
    mutationFn: async () => {
      const body = {
        titularId: titular!.id,
        tramitadorId: tramitador?.id ?? null,
        servicioCodigo: servicio,
        // RF-003: identidad comercial y técnica tal cual se capturó; solo se
        // recorta el espacio sobrante.
        otroServicio: servicio === OTRO ? otroServicio.trim() : null,
        productoNombre: productoNombre.trim(),
        marca: marca.trim(),
        modelos: modelos.trim() || null,
        accesorios: accesoriosTexto.trim() || null,
        producto: tieneParametros(producto) ? producto : null,
        listaAccesorios: accesorios.map((a) => ({
          nombre: a.nombre.trim(),
          voltajeEntrada: a.voltajeEntrada || null, voltajeSalida: a.voltajeSalida || null,
          corrienteEntrada: a.corrienteEntrada || null, corrienteSalida: a.corrienteSalida || null,
          frecuenciaEntrada: a.frecuenciaEntrada || null, frecuenciaSalida: a.frecuenciaSalida || null,
          potenciaEntrada: a.potenciaEntrada || null, potenciaSalida: a.potenciaSalida || null,
        })),
        paisesOrigenIds: paisesOrigen.length ? paisesOrigen : null,
        paisesProcedenciaIds: paisesProcedencia.length ? paisesProcedencia : null,
        fracciones: fracciones
          .filter((f) => f.numero.trim())
          .map((f) => ({ numero: f.numero.trim(), nico: f.nico.trim() || null })),
        labNombre: showLab ? labNombre.trim() || null : null,
        labNumInforme: showLab ? labNumInforme.trim() || null : null,
        labModelosEvaluados: showLab ? labModelosEvaluados.trim() || null : null,
        labFechaFinPruebas: showLab ? labFechaFinPruebas || null : null,
        labFechaEmision: showLab ? labFechaEmision || null : null,
        modelosAgrupar: modelosAgrupar.trim() || null,
        modeloRepresentativo: modeloRepresentativo.trim() || null,
        // El folio del dictamen sí se normaliza: es una clave, no un nombre comercial.
        certAntNumDictamen: showCertAnterior ? certAntNumDictamen.trim().toUpperCase() || null : null,
        certAntModsAmpliar: showCertAnterior ? certAntModsAmpliar.trim() || null : null,
        certAntCertCumpl: showCertAnterior ? certAntCertCumpl.trim().toUpperCase() || null : null,
        normas: normas
          .filter((n) => n.codigo.trim())
          .map((n) => ({
            nombreNorma: n.codigo,
            esquema: n.esquema.trim() || null,
            nmx: n.nmx,
          })),
        observaciones: observaciones.trim() || null,
        nombreRepresentante: nombreRepresentante.trim() || null,
        condicionesUso,
        firmaTipo: null,
        firmaObjectKey: null,
      };
      const { data } = await api.post<{ id: string }>("/api/sector/seguridad/solicitudes", body);
      return data.id;
    },
    onSuccess: (id) => navigate(`/sector/seguridad/solicitudes/${id}/success`),
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string; message?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.message || r.response?.data?.title || (e as Error).message);
    },
  });

  const validar = (): string | null => {
    if (!titular) return "Selecciona un titular.";
    if (!tramitadorBloqueado && numTramitadoresAut > 1 && !tramitador)
      return "Selecciona el tramitador autorizado para este titular.";
    if (!servicio) return "Selecciona el tipo de trámite.";
    if (servicio === OTRO && !otroServicio.trim()) return "Captura el texto de 'Otro'.";
    if (!productoNombre.trim() || !marca.trim()) return "Nombre del producto y marca son obligatorios.";
    if (!modelos.trim()) return "Captura los modelos del producto.";
    if (paisesOrigen.length === 0) return "Indica al menos un país de origen.";
    if (paisesProcedencia.length === 0) return "Indica al menos un país de procedencia.";
    const fraccionesValidas = fracciones.filter((f) => f.numero.trim());
    if (fraccionesValidas.length === 0) return "Captura al menos una fracción arancelaria.";
    for (const f of fraccionesValidas) {
      if (!/^\d{8}$/.test(f.numero.trim())) return `La fracción "${f.numero}" debe tener 8 dígitos.`;
      const nico = f.nico.trim();
      if (!(nico === "" || nico === "N/A" || /^\d{2}$/.test(nico))) return `El NICO "${nico}" debe ser de 2 dígitos, "N/A" o vacío.`;
    }
    if (showLab && labFechaFinPruebas && labFechaEmision && labFechaFinPruebas > labFechaEmision) {
      return "La fecha de fin de pruebas no puede ser posterior a la de emisión.";
    }
    const normasValidas = normas.filter((n) => n.codigo.trim());
    if (normasValidas.length === 0) return "Agrega al menos una norma.";
    for (const n of normasValidas) {
      if (!n.esquema.trim()) return `Selecciona el esquema de la norma "${n.codigo}".`;
      const cat = normaCatPorCodigo(n.codigo);
      if (cat && cat.nmx.length > 0 && n.nmx.length === 0) {
        return `La norma "${n.codigo}" requiere seleccionar al menos una NMX.`;
      }
    }
    // RF-005, validación 1: no se guarda una solicitud sin responsable.
    if (!nombreRepresentante.trim()) return "Selecciona o captura el nombre del representante.";
    if (!condicionesUso) return "Debes aceptar las condiciones de uso.";
    return null;
  };

  const submit = () => {
    const err = validar();
    if (err) { setError(err); return; }
    setError(null);
    crear.mutate();
  };

  // -- Render --
  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <Link to="/seguridad" className="inline-flex items-center gap-1 text-sm text-fg-muted transition hover:text-primary-strong">
        <ChevronLeft className="h-4 w-4" /> Volver a Seguridad
      </Link>

      <header>
        <h1 className="text-2xl font-semibold text-fg">Nueva solicitud de Seguridad</h1>
        <p className="text-sm text-fg-muted">
          Captura los datos del producto y la documentación. Si actúas vía tramitador externo,
          debe existir carta poder vigente.
        </p>
      </header>

      {/* Partes */}
      <Section title="Datos del solicitante">
        {ctxActivo && !ctxEsTramitadorPuro && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-estado-info/25 bg-estado-info-bg p-3 text-sm text-estado-info">
            <Building2 className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p>Estás creando una nueva solicitud para <strong>{ctxActivo.titularRazonSocial}</strong>.</p>
              <p className="mt-0.5 text-xs text-estado-info">
                Si necesitas trabajar con otra empresa, vuelve a Seguridad y cámbiala en el selector de empresa de la barra superior.
              </p>
            </div>
          </div>
        )}
        {ctxEsTramitadorPuro && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-estado-info/25 bg-estado-info-bg p-3 text-sm text-estado-info">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Estás actuando como tramitador ({ctxActivo?.titularRazonSocial}).</p>
              <p className="mt-0.5 text-xs text-estado-info">
                Selecciona la empresa <strong>titular cliente</strong> para la cual levantas esta solicitud.
              </p>
            </div>
          </div>
        )}
        <div className="space-y-3">
          <Field label="Titular *">
            {titularBloqueado ? (
              <ParteBloqueada
                razonSocial={titular?.razonSocial ?? ctxActivo?.titularRazonSocial ?? "—"}
                nota="Definido por tu empresa; no editable."
              />
            ) : (
              <TitularPicker value={titular} onChange={setTitular} />
            )}
          </Field>
          {tramitadorBloqueado ? (
            <Field label="Tramitador">
              <ParteBloqueada
                razonSocial={
                  tramitador?.razonSocial ??
                  ctxActivo?.tramitadorRazonSocial ??
                  ctxActivo?.titularRazonSocial ??
                  "—"
                }
                nota="Tramitador externo definido por carta poder; no editable."
              />
            </Field>
          ) : (
            <TramitadorAutorizadoField
              titularId={titular?.id}
              value={tramitador}
              onChange={setTramitador}
              onAutorizadosChange={setNumTramitadoresAut}
            />
          )}
        </div>
      </Section>

      {/* Servicio */}
      <Section title="Tipo de trámite">
        <Field label="Servicio *">
          <select value={servicio} onChange={(e) => setServicio(e.target.value)} className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg">
            <option value="">— Selecciona —</option>
            {qServicios.data?.map((s) => (
              <option key={s.codigo} value={s.codigo} title={s.descripcion}>{s.nombre}</option>
            ))}
          </select>
        </Field>
        {servicio === OTRO && (
          <Field label="Otro (especificar) *">
            <input value={otroServicio} onChange={(e) => setOtroServicio(e.target.value)}
                   maxLength={150} className="w-full rounded border px-2 py-1.5 text-sm" />
          </Field>
        )}
      </Section>

      {/* Producto */}
      <Section title="Datos del producto">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nombre del producto *">
            <input value={productoNombre} onChange={(e) => setProductoNombre(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Marca *">
            <input value={marca} onChange={(e) => setMarca(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Modelos *" span={2}>
            <input value={modelos} onChange={(e) => setModelos(e.target.value)} placeholder="separados por coma"
                   className="w-full rounded border px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Accesorios (texto)" span={2}>
            <input value={accesoriosTexto} onChange={(e) => setAccesoriosTexto(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" />
          </Field>
        </div>
        <div className="mt-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-fg-subtle">Parámetros eléctricos del producto</p>
          <ParametrosFields value={producto} onChange={setProducto} />
        </div>
      </Section>

      {/* Accesorios estructurados */}
      <Section title={`Accesorios${accesorios.length > 0 ? ` (${accesorios.length})` : ""}`}>
        <button type="button"
          onClick={() => setAccesorios((a) => [...a, { uiId: uid(), nombre: "", ...PARAMS_VACIOS }])}
          className="mb-3 inline-flex items-center gap-1 rounded border border-primary-strong px-3 py-1.5 text-sm font-medium text-primary-strong transition hover:bg-primary-tint">
          <Plus className="h-4 w-4" /> Agregar accesorio
        </button>
        {accesorios.length === 0 ? (
          <p className="text-xs text-fg-subtle">Sin accesorios estructurados (opcional).</p>
        ) : (
          <div className="space-y-3">
            {accesorios.map((a, i) => (
              <div key={a.uiId} className="rounded border bg-surface-2 p-3">
                <div className="mb-2 flex items-center gap-2">
                  <input value={a.nombre} placeholder="Nombre del accesorio"
                         onChange={(e) => setAccesorios((arr) => arr.map((x, idx) => idx === i ? { ...x, nombre: e.target.value } : x))}
                         className="flex-1 rounded border px-2 py-1.5 text-sm" />
                  <button type="button" onClick={() => setAccesorios((arr) => arr.filter((_, idx) => idx !== i))}
                          className="rounded p-1 text-estado-err transition hover:bg-estado-err-bg"><Trash2 className="h-4 w-4" /></button>
                </div>
                <ParametrosFields
                  value={a}
                  onChange={(p) => setAccesorios((arr) => arr.map((x, idx) => idx === i ? { ...x, ...p } : x))}
                />
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Países */}
      <Section title="Origen y procedencia">
        <div className="grid gap-3 sm:grid-cols-2">
          <PaisesPicker label="País(es) de origen *" opciones={qPaises.data ?? []} seleccion={paisesOrigen} onChange={setPaisesOrigen} required />
          <div className="space-y-2">
            <PaisesPicker label="País(es) de procedencia *" opciones={qPaises.data ?? []} seleccion={paisesProcedencia} onChange={setPaisesProcedencia} required />
            {paisesOrigen.length > 0 && !mismosPaises(paisesOrigen, paisesProcedencia) && (
              <button type="button" onClick={() => setPaisesProcedencia(paisesOrigen)}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-primary-strong bg-primary-tint px-3 py-2 text-sm font-medium text-primary-strong transition hover:bg-primary/15 hover:border-solid">
                <Copy className="h-4 w-4" /> Usar los mismos países de origen
                <span className="rounded-full bg-surface px-1.5 py-0.5 text-[10px] font-semibold">{paisesOrigen.length}</span>
              </button>
            )}
          </div>
        </div>
      </Section>

      {/* Fracciones arancelarias */}
      <Section title="Fracciones arancelarias *">
        <div className="space-y-2">
          {fracciones.map((f, i) => (
            <div key={f.uiId} className="flex items-center gap-2">
              <input value={f.numero} maxLength={8} placeholder="8 dígitos"
                     onChange={(e) => setFracciones((arr) => arr.map((x, idx) => idx === i ? { ...x, numero: e.target.value.replace(/\D/g, "") } : x))}
                     className="w-40 rounded border px-2 py-1.5 text-sm" />
              <input value={f.nico} maxLength={3} placeholder="NICO (2 díg / N/A)"
                     onChange={(e) => setFracciones((arr) => arr.map((x, idx) => idx === i ? { ...x, nico: e.target.value.toUpperCase() } : x))}
                     className="w-40 rounded border px-2 py-1.5 text-sm" />
              {fracciones.length > 1 && (
                <button type="button" onClick={() => setFracciones((arr) => arr.filter((_, idx) => idx !== i))}
                        className="rounded p-1 text-estado-err transition hover:bg-estado-err-bg"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setFracciones((arr) => [...arr, { uiId: uid(), numero: "", nico: "" }])}
                className="mt-2 inline-flex items-center gap-1 rounded border border-primary-strong px-3 py-1.5 text-sm font-medium text-primary-strong transition hover:bg-primary-tint">
          <Plus className="h-4 w-4" /> Agregar fracción
        </button>
      </Section>

      {/* Laboratorio — condicional por tipo de servicio */}
      {showLab && (
        <Section title="Datos del laboratorio">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nombre del laboratorio">
              <input value={labNombre} onChange={(e) => setLabNombre(e.target.value)} className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg" />
            </Field>
            <Field label="Número de informe">
              <input value={labNumInforme} onChange={(e) => setLabNumInforme(e.target.value)} className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg" />
            </Field>
            <Field label="Modelos evaluados" span={2}>
              <input value={labModelosEvaluados} onChange={(e) => setLabModelosEvaluados(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" />
            </Field>
            <Field label="Fecha fin de pruebas">
              <input type="date" value={labFechaFinPruebas} onChange={(e) => setLabFechaFinPruebas(e.target.value)} className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg" />
            </Field>
            <Field label="Fecha de emisión">
              <input type="date" value={labFechaEmision} onChange={(e) => setLabFechaEmision(e.target.value)} className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg" />
            </Field>
          </div>
        </Section>
      )}

      {/* Agrupación / certificado anterior */}
      <Section title="Agrupación y antecedentes">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Modelos a agrupar">
            <input value={modelosAgrupar} onChange={(e) => setModelosAgrupar(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Modelo representativo">
            <input value={modeloRepresentativo} onChange={(e) => setModeloRepresentativo(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" />
          </Field>
          {showCertAnterior && (
            <>
              <Field label="No. de dictamen / certificado anterior">
                <input value={certAntNumDictamen} onChange={(e) => setCertAntNumDictamen(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm uppercase" />
              </Field>
              <Field label="Modelos a ampliar">
                <input value={certAntModsAmpliar} onChange={(e) => setCertAntModsAmpliar(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" />
              </Field>
              <Field label="Certificado de cumplimiento" span={2}>
                <input value={certAntCertCumpl} onChange={(e) => setCertAntCertCumpl(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm uppercase" />
              </Field>
            </>
          )}
        </div>
      </Section>

      {/* Normas / esquema / NMX — catálogo con selección dependiente */}
      <Section title={`Normas aplicables${normas.length > 0 ? ` (${normas.length})` : ""} *`}>
        <div className="space-y-3">
          {normas.map((n, i) => {
            const cat = normaCatPorCodigo(n.codigo);
            const usadas = normas.filter((_, idx) => idx !== i).map((x) => x.codigo);
            const opciones = (qNormasCat.data ?? []).filter((o) => !usadas.includes(o.codigo) || o.codigo === n.codigo);
            const setRow = (patch: Partial<NormaForm>) =>
              setNormas((arr) => arr.map((x, idx) => idx === i ? { ...x, ...patch } : x));
            return (
              <div key={n.uiId} className="rounded border bg-surface-2 p-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Norma *">
                    <select value={n.codigo}
                            onChange={(e) => setRow({ codigo: e.target.value, esquema: "", nmx: [] })}
                            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg">
                      <option value="">— Selecciona —</option>
                      {opciones.map((o) => (<option key={o.codigo} value={o.codigo}>{o.nombre}</option>))}
                    </select>
                  </Field>
                  <Field label="Esquema de certificación *">
                    <select value={n.esquema} disabled={!cat}
                            onChange={(e) => setRow({ esquema: e.target.value })}
                            className="w-full rounded border px-2 py-1.5 text-sm disabled:bg-surface-2">
                      <option value="">{cat ? "— Selecciona —" : "Selecciona una norma primero"}</option>
                      {cat?.esquemas.map((esq) => (<option key={esq} value={esq}>{esq}</option>))}
                    </select>
                  </Field>
                </div>

                {cat && cat.nmx.length > 0 && (
                  <Field label="NMX relacionadas * (al menos una)">
                    <div className="rounded border bg-surface p-2">
                      {cat.nmx.map((nmx) => {
                        const checked = n.nmx.includes(nmx);
                        return (
                          <label key={nmx} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs transition hover:bg-surface-2">
                            <input type="checkbox" checked={checked}
                                   onChange={() => setRow({ nmx: checked ? n.nmx.filter((x) => x !== nmx) : [...n.nmx, nmx] })} />
                            <span className="font-mono">{nmx}</span>
                          </label>
                        );
                      })}
                    </div>
                  </Field>
                )}

                {normas.length > 1 && (
                  <div className="mt-2 text-right">
                    <button type="button" onClick={() => setNormas((arr) => arr.filter((_, idx) => idx !== i))}
                            className="inline-flex items-center gap-1 rounded p-1 text-xs text-estado-err transition hover:bg-estado-err-bg">
                      <Trash2 className="h-4 w-4" /> Quitar norma
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <button type="button" onClick={() => setNormas((arr) => [...arr, { uiId: uid(), codigo: "", esquema: "", nmx: [] }])}
                className="mt-2 inline-flex items-center gap-1 rounded border border-primary-strong px-3 py-1.5 text-sm font-medium text-primary-strong transition hover:bg-primary-tint">
          <Plus className="h-4 w-4" /> Agregar norma
        </button>
      </Section>

      {/* Cierre */}
      <Section title="Observaciones y firma">
        {/* RF-005: propuesto desde el registro legal vigente del titular. */}
        <ResponsablePicker
          label="Nombre del representante"
          registroLegalId={titular?.id}
          value={nombreRepresentante}
          onChange={setNombreRepresentante}
          required
        />
        <Field label="Observaciones">
          <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value.slice(0, 1000))} rows={4} maxLength={1000}
                    className="w-full rounded border border-edge-strong px-2 py-1.5 text-sm focus:border-primary focus:outline-none" />
        </Field>
        <label className="inline-flex items-start gap-2 text-sm">
          <input type="checkbox" checked={condicionesUso} onChange={(e) => setCondicionesUso(e.target.checked)} className="mt-0.5" />
          <span>Acepto las <strong>condiciones de uso</strong> del servicio. *</span>
        </label>
      </Section>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border bg-estado-err-bg p-3 text-sm text-estado-err">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={() => navigate("/seguridad")} disabled={crear.isPending}
                className="rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2">Cancelar</button>
        <button onClick={submit} disabled={crear.isPending}
                className="inline-flex items-center gap-1 rounded bg-primary px-4 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50">
          <Save className="h-4 w-4" />
          {crear.isPending ? "Creando…" : "Crear solicitud"}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Subcomponentes
// ============================================================================
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border bg-surface p-4 shadow-card">
      <h2 className="border-b pb-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">{title}</h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children, span = 1 }: { label: string; children: React.ReactNode; span?: 1 | 2 | 3 }) {
  const cls = span === 3 ? "sm:col-span-3" : span === 2 ? "sm:col-span-2" : "";
  return (
    <div className={cls}>
      <label className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** Grilla de 8 parámetros eléctricos (entrada/salida). */
function ParametrosFields({ value, onChange }: { value: Parametros; onChange: (p: Parametros) => void }) {
  const campos: [keyof Parametros, string][] = [
    ["voltajeEntrada", "Voltaje entrada"], ["voltajeSalida", "Voltaje salida"],
    ["corrienteEntrada", "Corriente entrada"], ["corrienteSalida", "Corriente salida"],
    ["frecuenciaEntrada", "Frecuencia entrada"], ["frecuenciaSalida", "Frecuencia salida"],
    ["potenciaEntrada", "Potencia entrada"], ["potenciaSalida", "Potencia salida"],
  ];
  return (
    <div className="grid gap-2 sm:grid-cols-4">
      {campos.map(([k, label]) => (
        <div key={k}>
          <label className="text-[10px] uppercase tracking-wide text-fg-subtle">{label}</label>
          <input value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })}
                 className="mt-0.5 w-full rounded border px-2 py-1 text-sm" />
        </div>
      ))}
    </div>
  );
}

function tieneParametros(p: Parametros): boolean {
  return Object.values(p).some((v) => v.trim() !== "");
}

function mismosPaises(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = new Set(a);
  return b.every((id) => sa.has(id));
}
